import type { Edge, Node } from '@xyflow/react'
import { ChevronLeft } from 'lucide-react'
import { useLocation } from 'router-v6'
import { FlowStatusBadge } from 'v2/shared/components/flow-status-badge'
import { useFlowId } from '../../hooks/use-flow-id'
import { useFlowStore } from '../../store'
import type { FlowData, NodeDataBase } from '../../types'
import { checkFlowUnsavedChangesForNavigation } from '../../utils'
import { ValidationErrorIndicator } from '../validation-error-indicator'
import { VibeAgentToggleButton } from '../vibe-agent/components/toggle-button'
import { FlowActionButtons } from './flow-action-buttons'
import { FlowTitleEditor } from './flow-title-editor'
import { useAppNavigate } from 'v2/shared/hooks/use-app-navigate'

interface TitleBarProps {
  flowData?: FlowData
  pasteContent: (
    nodes: Node<NodeDataBase<unknown>>[],
    edges: Edge[],
    sourceDescription: string,
  ) => void
}

export const TitleBar: React.FC<TitleBarProps> = ({ flowData, pasteContent }) => {
  const navigate = useAppNavigate()
  const location = useLocation()
  const { isNew: isNewFlow } = useFlowId()
  const storeFlowName = useFlowStore((state) => state.currentFlowName)
  const badgeState = isNewFlow ? 'unsaved' : flowData?.phlo?.state

  const isDefault = !isNewFlow && !!flowData?.phlo?.is_default

  const safeNavigate = (to: string, options?: { replace?: boolean; state?: string }) => {
    checkFlowUnsavedChangesForNavigation(true, () => {
      // Check if we came from agent/flow-runs/ using location state
      const navigationState = location.state as { from?: string; fromFlowsListing?: boolean }
      if (navigationState?.from?.includes('agent/flow-runs/') || navigationState?.fromFlowsListing)
        navigate(-1)
      else navigate(to, options)
    })
  }

  const nameToUse = () =>
    // Use store name if available, otherwise fall back to flow data
    storeFlowName || flowData?.phlo?.name || ''

  return (
    <div
      data-testid="title-bar"
      className="h-[69px] border-l-0 place-items-center flex justify-between gap-2 p-4"
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <ChevronLeft
          size={24}
          onClick={() => safeNavigate('/agents')}
          className="cursor-pointer transition-colors hover:text-primary"
        />
        <FlowTitleEditor flowData={flowData} isDefault={isDefault} isNewFlow={isNewFlow} />
        {badgeState && (
          <div className="ml-2">
            <FlowStatusBadge state={badgeState} />
          </div>
        )}
        {flowData && <ValidationErrorIndicator flowData={flowData} />}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <VibeAgentToggleButton />
        <FlowActionButtons flowData={flowData} name={nameToUse()} pasteContent={pasteContent} />
      </div>
    </div>
  )
}
